import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type ComparisonRow = {
  label: string
  agency: string
  quiteright: string
  highlight?: boolean
}

const COMPARISON: ComparisonRow[] = [
  {
    label: "Monthly cost",
    agency: "\u00A31,500\u2013\u00A35,000+",
    quiteright: "From \u00A349/month",
    highlight: true,
  },
  {
    label: "Contract length",
    agency: "6\u201312 month minimum",
    quiteright: "Cancel any time",
    highlight: true,
  },
  {
    label: "Turnaround time",
    agency: "5\u201310 working days",
    quiteright: "60 seconds",
    highlight: true,
  },
  {
    label: "Videos per month",
    agency: "4\u20138 (depends on retainer)",
    quiteright: "Every listing, unlimited",
  },
  {
    label: "Revisions",
    agency: "1\u20132 rounds included",
    quiteright: "Instant, unlimited",
  },
  {
    label: "Briefing process",
    agency: "Calls, briefs, approval chains",
    quiteright: "Paste a URL, done",
  },
  {
    label: "Brand consistency",
    agency: "Depends on the creative",
    quiteright: "Automatic on every video",
  },
  {
    label: "Multi-platform formats",
    agency: "Extra charge per format",
    quiteright: "Reels, TikTok, Shorts, landscape",
  },
  {
    label: "Voiceover",
    agency: "Extra cost per recording",
    quiteright: "Included, multiple voices",
  },
  {
    label: "Scale to 50+ listings",
    agency: "Major retainer increase",
    quiteright: "No extra effort",
    highlight: true,
  },
  {
    label: "In-house control",
    agency: "You depend on their schedule",
    quiteright: "Your team, your timing",
    highlight: true,
  },
]

const HIDDEN_COSTS = [
  {
    label: "Retainer",
    detail: "Most agencies charge \u00A31,500\u2013\u00A33,000/month as a base retainer before any production work begins. That's \u00A318,000\u2013\u00A336,000 a year for the privilege of being a client.",
  },
  {
    label: "Per-video production",
    detail: "On top of the retainer, each video typically costs \u00A3200\u2013\u00A3500 to produce. Ten videos a month? That's another \u00A32,000\u2013\u00A35,000.",
  },
  {
    label: "Platform reformatting",
    detail: "Need the same video in portrait for Reels and landscape for YouTube? That's a separate line item. Agencies charge \u00A350\u2013\u00A3150 per format adaptation.",
  },
  {
    label: "Strategy and reporting",
    detail: "Monthly strategy calls, performance reports, and content calendars sound valuable. But you're paying \u00A3200\u2013\u00A3500/month for insights you could get from free analytics tools.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Is a marketing agency worth it for estate agents?",
    a: "It depends on what you need. If you want full-service marketing (branding, print, PR, events), an agency can help. But if your primary goal is getting video content onto social media for every listing, you're paying agency overhead for something QuiteRight does in 60 seconds. Most agents find that a good CRM, a portal presence, and QuiteRight cover 90% of their marketing needs.",
  },
  {
    q: "What does a property marketing agency actually deliver?",
    a: "Typically: a content calendar, 4\u20138 social media posts per month, some video editing, monthly reports, and strategy calls. The problem is that 4\u20138 pieces of content barely scratches the surface when you have 20+ active listings, each needing its own video across multiple platforms.",
  },
  {
    q: "How much should an estate agent spend on marketing?",
    a: "Industry benchmarks suggest 5\u201310% of gross commission income. For a branch doing \u00A3300k in fees, that's \u00A315,000\u2013\u00A330,000 a year. The question is whether that budget is better spent on an agency retainer or on tools that let your team produce content at scale. QuiteRight's Managed plan costs \u00A33,588/year and covers every listing.",
  },
  {
    q: "Can I use QuiteRight alongside a marketing agency?",
    a: "Absolutely. Many agents use an agency for brand strategy, print materials, and PR, then use QuiteRight for day-to-day listing video production. It means your agency can focus on high-value creative work instead of churning out repetitive listing videos.",
  },
  {
    q: "What if my agency already creates property videos?",
    a: "Ask them how many they produce per month and what each one costs. If the answer is fewer than your total listings, or more than \u00A350 per video, QuiteRight fills the gap. Every listing deserves video marketing, not just the ones your agency has bandwidth for.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function ComparisonTable({ rows }: { rows: ComparisonRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[540px] text-left">
        <thead>
          <tr className="border-b-2 border-stone">
            <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              Feature
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em] text-slate">
              Marketing Agency
            </th>
            <th className="pb-3 pl-4 text-center text-xs font-medium uppercase tracking-[0.15em] text-red">
              QuiteRight
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-stone/50 ${row.highlight ? "bg-cream/50" : ""}`}
            >
              <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                {row.label}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.agency}
              </td>
              <td className="py-3.5 pl-4 text-center text-sm font-semibold text-charcoal">
                {row.quiteright}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function QuiterightVsAgency({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "QuiteRight vs Marketing Agency: Which Delivers More Listings?",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "QuiteRight vs Agency" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Compare
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                Retainers buy meetings, not results
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                A marketing agency gives you strategy decks and content calendars.
                QuiteRight gives you a video for every listing, live on socials before
                the ink dries on the instruction.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-left-2">
                <p className="text-xs font-medium text-warm-grey">From</p>
                <p className="font-display text-xl font-bold text-white">&pound;49/month</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hidden costs */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The fine print
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              What agency pricing actually looks like
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2">
            {HIDDEN_COSTS.map((item, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <p className="font-display text-lg font-bold text-charcoal">{item.label}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.detail}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Comparison table */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Side by side
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              The full comparison
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <ComparisonTable rows={COMPARISON} />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* The maths */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The maths
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-white md:text-4xl">
              Agency retainers vs QuiteRight
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  Agency: 8 videos/month
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-white">
                  &pound;2,500+
                </p>
                <p className="mt-1 text-sm text-warm-grey">retainer plus production</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-red/30 bg-red/10 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-red">
                  QuiteRight: every listing
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-white">
                  &pound;299
                </p>
                <p className="mt-1 text-sm text-warm-grey">50 videos, all platforms</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  Annual saving
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-red">
                  &pound;26,000+
                </p>
                <p className="mt-1 text-sm text-warm-grey">with more videos produced</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* When to use each */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Honest advice
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              When to use which
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Use an agency when
                  <span className="text-red">:</span>
                </h3>
                <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-slate/50">&bull;</span>
                    You need a complete rebrand, new website, or print campaign
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-slate/50">&bull;</span>
                    You want PR, press coverage, and media buying
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-slate/50">&bull;</span>
                    You lack any in-house marketing resource and need strategic direction
                  </li>
                </ul>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-red/20 bg-red/5 p-6">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Use QuiteRight when
                  <span className="text-red">:</span>
                </h3>
                <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-red">&bull;</span>
                    You want every listing marketed with video on social media
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-red">&bull;</span>
                    Speed matters: videos live on day one, not after a two-week production cycle
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-red">&bull;</span>
                    You want consistent, branded content without briefing anyone
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-red">&bull;</span>
                    You want to impress vendors at market appraisals with instant video
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-red">&bull;</span>
                    You want to keep &pound;26,000+ a year in your business
                  </li>
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      <RelatedPages links={relatedFor("quiteright-vs-agency")} tone="white" />

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Cut the retainer. Keep the results
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Every listing deserves a video. QuiteRight delivers them in seconds,
              not weeks, without a retainer, a brief, or a meeting.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
